import { portfolioHeroVideo, portfolioHeroPoster } from "@/lib/media-assets";
import { LazyVideo } from "@/components/ui/lazy-video";
import { useState, useEffect, useRef, useCallback } from "react";
import { openBookingLightbox } from "@/components/booking/booking-lightbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, Clock, Mail, Gift, ArrowRight, X } from "lucide-react";
import {
  ensureUrgencyOffer,
  claimTenPercent,
  formatCountdown,
  type UrgencyOffer,
} from "@/lib/promo";
import { trackEvent } from "@/lib/meta-pixel";

// booking-widget.tsx listens for this event and applies the code to the quote.
export const APPLY_COUPON_EVENT = "apply-stay5-coupon";

/** Current Promotions (and other CTAs) dispatch this to open the discount lightbox. */
export const OPEN_EMAIL_OFFER_EVENT = "open-email-offer";

const SESSION_DISMISSED_KEY = "vro_offer_dismissed"; // set only when the visitor closes it
const EMAIL_CODE_KEY = "vro_email_coupon"; // remember a claimed 5% code across refreshes

// On a phone the lightbox opens by itself only for a visitor this far into the page.
const ENGAGED_DELAY_MS = 40_000;
const ENGAGED_SCROLL_DEPTH = 0.6;

interface BookingOfferPopupProps {
  /**
   * When true (booking page default), auto-open after delay / exit-intent and show
   * the floating re-open tab after dismiss. When false, only opens via
   * OPEN_EMAIL_OFFER_EVENT (homepage / stay promotions CTA).
   */
  autoTrigger?: boolean;
}

function wasDismissed(): boolean {
  try {
    return Boolean(sessionStorage.getItem(SESSION_DISMISSED_KEY));
  } catch {
    return false;
  }
}

function scrollToBooking() {
  // Straight into the booking lightbox for the home on screen (or the home
  // chooser), rather than scrolling in search of a form or leaving the page.
  setTimeout(() => {
    const slug = window.location.pathname.match(/^\/properties\/([^/?#]+)/)?.[1] ?? null;
    openBookingLightbox({ slug });
  }, 120);
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) && value.trim().length <= 254;
}

export function BookingOfferPopup({ autoTrigger = true }: BookingOfferPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showTab, setShowTab] = useState(false);

  const [offer, setOffer] = useState<UrgencyOffer | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [emailCode, setEmailCode] = useState<string | null>(null);
  const [emailError, setEmailError] = useState("");
  const [copied, setCopied] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const hasTriggeredRef = useRef(false);

  const tenActive = offer !== null && secondsLeft > 0;

  // Load (or mint) the urgency offer so the countdown reflects the true server window.
  useEffect(() => {
    let mounted = true;
    ensureUrgencyOffer().then((o) => {
      if (mounted) setOffer(o);
    });
    // Restore a previously claimed 5% code so returning visitors still see it.
    try {
      const savedCode = localStorage.getItem(EMAIL_CODE_KEY);
      if (savedCode) {
        setEmailCode(savedCode);
        setEmailStatus("done");
      }
    } catch {
      /* ignore */
    }
    return () => {
      mounted = false;
    };
  }, []);

  // Drive the visible countdown from the server-authored expiry.
  useEffect(() => {
    if (!offer) return;
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.floor((offer.expiresAt - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [offer]);

  const openPopup = useCallback(() => {
    setIsOpen(true);
    setShowTab(false);
  }, []);

  const triggerPopup = useCallback(() => {
    if (hasTriggeredRef.current) return;
    // Respect a manual dismiss this session: fall back to the unobtrusive tab.
    if (wasDismissed()) {
      setShowTab(true);
      return;
    }
    hasTriggeredRef.current = true;
    setShowTab(false);
    setIsOpen(true);
  }, []);

  // Never interrupt the booking flow. This used to auto-open the full lightbox 5 seconds
  // after landing, which on a phone (96% of ad traffic) covered the booking form before
  // the visitor had seen a single price. Now the offer is always one tap away in the
  // floating tab, and the full lightbox opens on desktop exit-intent or, on a phone, for
  // a visitor who has read most of the page.
  useEffect(() => {
    if (!autoTrigger) return;
    setShowTab(true);
    if (wasDismissed()) return;

    const handleMouseOut = (e: MouseEvent) => {
      if (e.relatedTarget === null && e.clientY <= 0) triggerPopup();
    };
    document.addEventListener("mouseout", handleMouseOut);

    // A phone has no exit-intent, so with the 5 second auto-open gone the lightbox reached
    // almost nobody there and email signups fell from about 19 a week to 1. Open it once
    // for a visitor who has read well past the booking form without using it. Anyone who
    // touched the form, already holds a code, or has another dialog open is left alone.
    const isTouch = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    let longEnough = false;
    let usedBookingForm = false;
    const hasCode = () => {
      try {
        return Boolean(localStorage.getItem(EMAIL_CODE_KEY));
      } catch {
        return false;
      }
    };
    const openForEngagedReader = () => {
      if (!longEnough || usedBookingForm || hasCode()) return;
      if (document.querySelector('[role="dialog"]')) return;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0 || window.scrollY / scrollable < ENGAGED_SCROLL_DEPTH) return;
      triggerPopup();
    };
    const handlePointerDown = (e: PointerEvent) => {
      const form = document.getElementById("booking-form");
      if (form && e.target instanceof Node && form.contains(e.target)) usedBookingForm = true;
    };
    let engagedTimer = 0;
    if (isTouch) {
      engagedTimer = window.setTimeout(() => {
        longEnough = true;
        openForEngagedReader();
      }, ENGAGED_DELAY_MS);
      window.addEventListener("scroll", openForEngagedReader, { passive: true });
      document.addEventListener("pointerdown", handlePointerDown, true);
    }

    return () => {
      document.removeEventListener("mouseout", handleMouseOut);
      window.clearTimeout(engagedTimer);
      window.removeEventListener("scroll", openForEngagedReader);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [autoTrigger, triggerPopup]);

  // Open from Current Promotions "Get discount" (and similar) CTAs.
  useEffect(() => {
    const onOpen = () => openPopup();
    window.addEventListener(OPEN_EMAIL_OFFER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EMAIL_OFFER_EVENT, onOpen);
  }, [openPopup]);

  const dismiss = useCallback(() => {
    if (autoTrigger) {
      try {
        sessionStorage.setItem(SESSION_DISMISSED_KEY, "1");
      } catch {
        /* ignore */
      }
      setShowTab(true);
    }
    setIsOpen(false);
  }, [autoTrigger]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) dismiss();
      else openPopup();
    },
    [dismiss, openPopup]
  );

  const applyCode = useCallback((code: string) => {
    setIsOpen(false);
    setShowTab(true);
    window.dispatchEvent(new CustomEvent(APPLY_COUPON_EVENT, { detail: { code } }));
    scrollToBooking();
  }, []);

  // Claim this visitor's unique 10% code from the server, then apply it. If the window has
  // closed in the meantime, drop the 10% offer so the popup falls back to the 5% path.
  const claimAndApplyTen = useCallback(async () => {
    setClaiming(true);
    const result = await claimTenPercent();
    setClaiming(false);
    if (result?.code) {
      applyCode(result.code);
    } else {
      setOffer(null);
    }
  }, [applyCode]);

  const submitEmail = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setEmailError("");
      if (!isValidEmail(email)) {
        setEmailError("Please enter a valid email address.");
        setEmailStatus("error");
        return;
      }
      setEmailStatus("loading");
      try {
        const res = await fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.code) {
          setEmailError(data?.message || "Something went wrong. Please try again.");
          setEmailStatus("error");
          return;
        }
        setEmailCode(data.code);
        setEmailStatus("done");
        // Only a genuinely new subscriber counts as a Lead conversion for Meta.
        if (!data.alreadySubscribed) {
          trackEvent("Lead", {
            content_name: "Booking page 5% email offer",
            content_category: "email_signup",
          });
        }
        try {
          localStorage.setItem(EMAIL_CODE_KEY, data.code);
        } catch {
          /* ignore */
        }
      } catch {
        setEmailError("Something went wrong. Please try again.");
        setEmailStatus("error");
      }
    },
    [email]
  );

  const handleCopy = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  return (
    <>
      {/* Floating re-open tab: unobtrusive, keeps the offer one tap away after dismiss. */}
      {showTab && !isOpen && (
        <button
          type="button"
          onClick={openPopup}
          // Below lg the sticky "Book Your Stay" bar owns the bottom edge, so
          // the tab sits just above it instead of on top of it.
          className="fixed bottom-[calc(5.75rem+env(safe-area-inset-bottom))] left-4 lg:bottom-5 lg:left-5 z-40 inline-flex items-center gap-2 rounded-full bg-ink text-white shadow-lg hover:bg-ink transition-all px-4 py-2.5 font-serif text-luxury-sm animate-in fade-in slide-in-from-bottom-3"
          data-testid="button-offer-tab"
          aria-label="View your booking offer"
        >
          <Gift className="h-4 w-4" aria-hidden="true" />
          {tenActive ? (
            <span className="tabular-nums">Save 10% · {formatCountdown(secondsLeft)}</span>
          ) : (
            <span>Your discount</span>
          )}
        </button>
      )}

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="[&>button]:hidden max-w-md w-[calc(100%-2rem)] sm:w-full rounded-2xl border-sand bg-background p-0 overflow-hidden"
          data-testid="booking-offer-popup"
        >
          {/* Exact homepage coast asset; bright image, with copy on its own surface. */}
          <div className="relative overflow-hidden text-center text-ink">
            <LazyVideo
              className="w-full h-full object-cover"
              style={{height:144, aspectRatio:"auto"}}
              src={portfolioHeroVideo}
              poster={portfolioHeroPoster}
              autoPlay
              loop
              muted
              playsInline
              aria-hidden="true"
            />

            <div className="relative z-10 px-6 pt-5 pb-6">
            <button
              type="button"
              onClick={dismiss}
              className="absolute right-3 top-3 rounded-full p-1.5 text-ink hover:text-forest hover:bg-sand transition-colors"
              aria-label="Close offer"
              data-testid="button-close-offer"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>

            {tenActive ? (
              <>
                <div className="inline-flex items-center gap-1.5 bg-sand rounded-full px-3 py-1 text-luxury-xs font-serif mb-3 backdrop-blur-sm">
                  <Clock className="h-3 w-3" aria-hidden="true" />
                  Limited-time flash offer
                </div>
                <DialogTitle className="text-luxury-3xl font-serif font-normal tracking-luxury-tight mb-1">
                  Save 10% with your direct-booking offer
                </DialogTitle>
                <DialogDescription className="text-luxury-sm text-ink-soft font-serif">
                  Book direct before your offer expires for 10% off the eligible rental amount. Taxes, fees, and other offer terms apply.
                </DialogDescription>

                {/* Live countdown */}
                <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-sand px-4 py-2">
                  <Clock className="h-4 w-4 text-forest" aria-hidden="true" />
                  <span
                    className="font-serif text-luxury-xl tabular-nums tracking-wide"
                    data-testid="offer-countdown"
                    aria-live="off"
                  >
                    {formatCountdown(secondsLeft)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-1.5 bg-sand rounded-full px-3 py-1 text-luxury-xs font-serif mb-3 backdrop-blur-sm">
                  <Gift className="h-3 w-3" aria-hidden="true" />
                  Direct-booking offer
                </div>
                <DialogTitle className="text-luxury-2xl font-serif font-normal tracking-luxury-tight mb-1">
                  Save on your Oahu stay
                </DialogTitle>
                <DialogDescription className="text-luxury-sm text-ink-soft font-serif">
                  Enter your email for 5% off. Your SAVE5 code is valid for 6 months.
                </DialogDescription>
              </>
            )}
            </div>
          </div>

          <div className="p-6 sm:p-7">
            {/* Primary action: apply the 10% while the window is open */}
            {tenActive && (
              <>
                <Button
                  type="button"
                  variant="bronze"
                  className="w-full font-serif font-normal tracking-luxury-tight py-3 h-12 rounded-lg"
                  onClick={claimAndApplyTen}
                  disabled={claiming}
                  data-testid="button-apply-ten"
                >
                  {claiming ? "Applying…" : "Claim 10% off & book now"}
                  {!claiming && <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />}
                </Button>
                <p className="text-center text-luxury-xs text-muted-foreground font-serif mt-2">
                  Applies automatically at checkout, while the offer window is open.
                </p>

                <div className="flex items-center gap-3 my-5" aria-hidden="true">
                  <div className="h-px flex-1 bg-sand" />
                  <span className="text-luxury-xs text-muted-foreground font-serif">or</span>
                  <div className="h-px flex-1 bg-sand" />
                </div>
              </>
            )}

            {/* Email capture for the 5% code (valid 6 months) */}
            {emailStatus !== "done" ? (
              <form onSubmit={submitEmail} className="text-center">
                <div className="inline-flex items-center gap-1.5 text-ink font-serif text-luxury-sm mb-2">
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {tenActive ? "Not ready yet? Save 5% for later" : "Get 5% off, valid 6 months"}
                </div>
                <p className="text-luxury-xs text-muted-foreground font-serif mb-3">
                  We&apos;ll email your SAVE5 code instantly. Valid for 6 months. No spam, unsubscribe anytime.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch gap-2">
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailStatus === "error") setEmailStatus("idle");
                    }}
                    className="flex-1 h-11 font-serif"
                    aria-label="Email address"
                    aria-invalid={emailStatus === "error"}
                    data-testid="input-offer-email"
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    className="h-11 font-serif font-normal border-sand-deep shrink-0"
                    disabled={emailStatus === "loading"}
                    data-testid="button-submit-email"
                  >
                    {emailStatus === "loading" ? "Sending…" : "Get 5% off"}
                  </Button>
                </div>
                {emailError && (
                  <p className="text-luxury-xs text-destructive font-serif mt-2" data-testid="offer-email-error">
                    {emailError}
                  </p>
                )}
              </form>
            ) : (
              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 text-secondary font-serif text-luxury-sm mb-2">
                  <Check className="h-4 w-4" aria-hidden="true" />
                  Your 5% code is ready
                </div>
                <div className="flex items-stretch justify-center gap-2 mb-3">
                  <div
                    className="flex items-center justify-center border-2 border-dashed border-driftwood bg-shell/60 rounded-lg px-4 py-2.5 font-serif text-luxury-lg tracking-[0.15em] text-ink select-all"
                    data-testid="offer-email-code"
                  >
                    {emailCode}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="font-serif font-normal shrink-0 h-auto border-sand-deep"
                    onClick={() => emailCode && handleCopy(emailCode)}
                    aria-label={copied ? "Code copied" : "Copy your discount code"}
                    data-testid="button-copy-email-code"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1.5 text-secondary" aria-hidden="true" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1.5" aria-hidden="true" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-luxury-xs text-muted-foreground font-serif mb-3">
                  Valid for 6 months. We also emailed it to you.
                </p>
                <Button
                  type="button"
                  variant="bronze"
                  className="w-full font-serif font-normal tracking-luxury-tight py-3 h-11 rounded-lg"
                  onClick={() => emailCode && applyCode(emailCode)}
                  data-testid="button-apply-email-code"
                >
                  Apply 5% &amp; book now
                </Button>
              </div>
            )}

            <button
              type="button"
              onClick={dismiss}
              className="block mx-auto mt-4 text-luxury-xs text-muted-foreground hover:text-foreground font-serif underline underline-offset-2 transition-colors"
              data-testid="button-dismiss-offer"
            >
              No thanks, continue to booking
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
